import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { TableModule } from 'primeng/table';

import { Clipboard } from '@angular/cdk/clipboard';
import { RouterModule } from '@angular/router';
import { LEAD_TYPE } from '@core/enum/lead';
import { NoteExportFieldEnum, NoteReportColumn, noteReportColumns, NoteSortFieldEnum } from '@core/enum/report';
import { GetNoteParams, NoteDetailsReport, NoteReport } from '@core/models/interfaces/report';
import { ReportService } from '@core/services/report.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomPaginatorComponent } from '@shared/components/custom-paginator/custom-paginator.component';
import { EmptyTableComponent } from '@shared/components/empty-table/empty-table.component';
import { SortTableHeaderComponent } from '@shared/components/sort-table-header/sort-table-header.component';
import { FrozenColStyleDirective } from '@shared/directives/frozen-col-style.directive';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { PhoneNumberPipe } from '../../../../../shared/pipes/phone-number.pipe';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { NOTE_EXPORT_TO_SORT_FIELD } from '@core/constants/report';

@Component({
  selector: 'app-note-item-report',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    TranslateModule,
    CustomPaginatorComponent,
    EmptyTableComponent,
    FrozenColStyleDirective,
    SortTableHeaderComponent,
    TooltipModule,
    RouterModule,
    SafeHtmlPipe,
    PhoneNumberPipe
  ],
  templateUrl: './note-item-report.component.html',
  styleUrl: './note-item-report.component.scss'
})
export class NoteItemReportComponent {
  @Input() report: NoteDetailsReport;
  @Input() params: GetNoteParams;
  @Input() groupBy: string = '';
  @Input() index: number = 0;

  @Output() onSortChange = new EventEmitter<{ field: string; order: number }>();

  readonly translatePrefix = 'report.';
  readonly NoteSortFieldEnum = NoteSortFieldEnum;
  readonly LeadType = LEAD_TYPE;
  readonly NoteExportFieldEnum = NoteExportFieldEnum;
  readonly NOTE_EXPORT_TO_SORT_FIELD = NOTE_EXPORT_TO_SORT_FIELD

  isExpanded: boolean = true;
  data: NoteReport[] = [];
  pagination = {
    page: 0,
    size: 10
  };
  options: number[] = [5, 10, 20, 100];
  sortField: string = '';
  sortOrder: number = 0;
  isLoading: boolean = false;

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
    private clipboard: Clipboard,
    private translateService: TranslateService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['report']?.currentValue) {
      this.isLoading = false;
      this.data = [...this.report.data?.content];
      this.pagination = {
        size: 10,
        page: 0
      };
    }
  }

  onToggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  onPagination() {
    this.isLoading = true;
    this.reportService
      .getNoteReportById(this.report.id, {
        ...this.params,
        groupBy: this.params.groupBy,
        page: this.pagination.page,
        size: this.pagination.size
      })
      .subscribe({
        next: res => {
          this.isLoading = false;
          this.data = res.data.content;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  onSort(event: { field: string; order: number }) {
    if (this.isLoading) return;
    this.sortField = event.field;
    this.sortOrder = event.order;
    this.isLoading = true;
    this.onSortChange.emit({ field: this.sortField, order: this.sortOrder });
  }

  onCopy(value: string, label: string) {
    this.clipboard.copy(value.replaceAll('|', ''));
    this.messageService.add({
      severity: 'success',
      detail: this.translateService.instant('lead-details.overview-detail.copy-success', {
        value: this.translateService.instant('lead-details.overview-detail.' + label)
      })
    });
  }

  getLastReplyNote(item: NoteReport) {
    if (!item.replyNotes || item.replyNotes.length === 0) {
      return null;
    }

    const last = item.replyNotes[item.replyNotes.length - 1];
    return last.updatedAt;
  }

  get visibleColumns(): NoteReportColumn[] {
    return noteReportColumns.filter(col => {
      const index = this.params.visibleFields?.findIndex(e => e === NoteExportFieldEnum[col.key])
      return index !== -1
    })
  }
}
